<template>
  <fragment>
    <div class="flex-row">
      <div class="flex-column">
        <div class="sac__font-14">
          Payment method
        </div>
        <SACDropdown
          field="paymentMethodId"
          placeholder="Select a payment method"
          :options="paymentMethodOptions(branch.id)"
          :value="selectedPaymentMethods[branch.id]"
          :change="(f, v) => handlePaymentMethod(branch.id, v)"
        />
      </div>
      <div class="flex-column margin-left-18">
        <div class="sac__font-14">
          Tax status
        </div>
        <SACDropdown
          field="exempt"
          placeholder="Select a payment method"
          :options="[{
            label: 'No exemption or resale certificate',
            value: false,
          }, {
            label: 'Exempt',
            value: true,
          }]"
          :value="allTaxExemptions[branch.id]?.exempt || false"
          :change="(f, v) => handleTaxExempt(branch.id, f, v)"
          :disable="!!allTaxExemptions[branch.id]?.documentUrl"
        />
      </div>
    </div>
    <div
      v-if="allTaxExemptions[branch.id]?.exempt"
      class="flex-row margin-top-24"
    >
      <div class="flex-column">
        <div class="sac__font-14">
          Category
        </div>
        <SACDropdown
          field="reason"
          searchParameter="reason"
          placeholder="Select category"
          :options="taxExemptCategory"
          optionValue="code"
          optionLabel="reason"
          :value="allTaxExemptions[branch.id]?.reason"
          :change="(f, v) => handleTaxExempt(branch.id, f, v)"
        />
      </div>
      <div class="flex-column margin-left-18">
        <div class="sac__font-14">
          Tax Number
        </div>
        <SACInput
          field="number"
          placeholder="Enter tax number"
          :value="allTaxExemptions[branch.id]?.number"
          :change="(f, v) => handleTaxExempt(branch.id, f, v)"
        />
      </div>
      <div class="flex-column margin-left-18">
        <div class="sac__font-14">
          Expiration Date
        </div>
        <DatePicker
          v-model="taxExpiration"
        />
      </div>
      <div
        class="flex-row flex-1 align-center justify-end"
      >
        <a href="#" @click="downloadPdf(branch.id)">
          {{ taxDocName(branch.id) }}
        </a>
        <button
          v-if="allTaxExemptions[branch.id]?.tempFileName"
          type="button"
          @click="clearTaxExempt(branch.id)"
        >
          <i class="icon-Trash sac__font-18 tomato margin-left-4" />
        </button>
      </div>
    </div>
  </fragment>
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';

import SACDropdown from '../sac/SACDropdown.vue';
import SACInput from '../sac/SACInput.vue';
import DatePicker from '../DatePicker.vue';

export default {
  name: 'PaymentInfoSetting',
  components: {
    SACDropdown,
    SACInput,
    DatePicker,
  },
  props: {
    branch: Object,
  },
  computed: {
    ...mapState({
      allPaymentMethods: (state) => state.partnerAgreement.allPaymentMethods,
      selectedPaymentMethods: (state) => state.partnerAgreement.selectedPaymentMethods,
      allTaxExemptions: (state) => state.partnerAgreement.allTaxExemptions,
      taxExemptCategory: (state) => state.setting.taxExemptCategory,
    }),
    taxExpiration: {
      get() {
        if (this.allTaxExemptions[this.branch.id]) {
          return moment(this.allTaxExemptions[this.branch.id].expiration).toDate();
        }
        return null;
      },
      set(v) {
        this.handleTaxExempt(this.branch.id, 'expiration', v);
      },
    },
  },
  methods: {
    paymentMethodOptions(corpId) {
      const pms = this.allPaymentMethods[corpId];
      let pmOptions = [];
      if (pms?.allPaymentMethods.length > 0) {
        pmOptions = pms.allPaymentMethods.map((pm) => {
          const {
            id,
            card,
            bank_account: ba,
          } = pm;
          if (card) {
            return {
              label: `${card.brand.toUpperCase()} ending in ...${card.last4}`,
              value: id,
            };
          }
          return {
            label: `Bank ending in ...${ba.last4}`,
            value: id,
          };
        });
      }
      pmOptions.push({
        label: 'Add payment method',
        value: 'new',
      });
      return pmOptions;
    },
    handlePaymentMethod(corpId, pmId) {
      if (pmId === 'new') {
        this.$store.dispatch('changePartnerAgreementValues', [{
          field: 'showActionModal',
          value: true,
        }, {
          field: 'actionModalView',
          value: 'new-payment-method',
        }, {
          field: 'selectedCorpId',
          value: corpId,
        }]);
      } else {
        const newSelectedMethods = {
          ...this.selectedPaymentMethods,
          [corpId]: pmId,
        };
        this.$store.dispatch('changePartnerAgreementValues', [{
          field: 'selectedPaymentMethods',
          value: newSelectedMethods,
        }]);
      }
    },
    handleTaxExempt(corpId, field, value) {
      const newTaxExempt = {
        ...this.allTaxExemptions,
        [corpId]: {
          ...this.allTaxExemptions[corpId],
          [field]: value,
        },
      };
      if (field === 'exempt' && value) {
        this.$store.dispatch('changePartnerAgreementValues', [{
          field: 'showActionModal',
          value: true,
        }, {
          field: 'actionModalView',
          value: 'upload-tax-doc',
        }, {
          field: 'selectedCorpId',
          value: corpId,
        }]);
      } else {
        this.$store.dispatch('changePartnerAgreementValues', [{
          field: 'allTaxExemptions',
          value: newTaxExempt,
        }]);
      }
    },
    clearTaxExempt(corpId) {
      this.$store.dispatch('changePartnerAgreementValues', [{
        field: 'allTaxExemptions',
        value: {
          ...this.allTaxExemptions,
          [corpId]: {
            ...this.allTaxExemptions[corpId],
            exempt: false,
            tempFile: null,
            tempFileName: '',
          },
        },
      }]);
    },
    async downloadPdf(corpId) {
      const {
        tempFile,
        tempFileName,
        documentUrl,
      } = this.allTaxExemptions[corpId];
      if (tempFile) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64String = event.target.result.split(',')[1];
          const downloadLink = document.createElement('a');
          downloadLink.href = `data:application/pdf;base64,${base64String}`;
          downloadLink.download = tempFileName;
          downloadLink.click();
        };
        reader.readAsDataURL(tempFile);
      } else {
        const regex = /[^/]+(?=\/*(?:\?|#|$))/;
        const [fileName] = documentUrl.match(regex);
        const base64String = await this.$store.dispatch('getDocResponse', {
          url: documentUrl,
        });
        const downloadLink = document.createElement('a');
        downloadLink.href = base64String;
        downloadLink.download = fileName;
        downloadLink.click();
      }
    },
    taxDocName(corpId) {
      const {
        tempFileName,
        documentUrl,
      } = this.allTaxExemptions[corpId];
      let docName = tempFileName;
      if (documentUrl) {
        const regex = /[^/]+(?=\/*(?:\?|#|$))/;
        [docName] = documentUrl.match(regex);
      }
      return docName;
    },
  },
};
</script>

<style lang="scss" scoped>
#sac-input-container, #sac-dropdown-container {
  min-width: 300px;
}

.date-picker-container {
  background-color: $NBLUE5;
  border-radius: 6px;
  height: 44px;
  min-width: 300px;
  display: grid;
  padding: 0 12px;
  align-items: center;
}
</style>
